/* global document */
import {
  ITagDescriptions,
  IAttributesType,
  ID_ATTRIBUTE,
} from './common';

/**
 * Globally sets tags in the <head>. Will not affect tags already in <head>, but will only affect
 * tags added through `commitTags`.
 *
 * The argument should be an object whos values are objects of the form:
 *
 * ```
 * {
 *   tag: 'tag name string',
 *   attributes: { key: value, key: value, ... },
 * }
 * ```
 *
 * To avoid unnecessarily creating/removing tags, the keys of the object should be consistent
 * between calls to `commitTags`. For example:
 *
 * ```
 * commitTags({
 *   description: {
 *     tag: 'meta',
 *     attributes: { name: 'description', content: 'first description' },
 *   },
 * });
 * commitTags({
 *   description: {
 *     tag: 'meta',
 *     attributes: { name: 'description', content: 'second description' },
 *   },
 * });
 * ```
 *
 * will just update the attributes without creating a new element.
 */
export default function commitTagsToDom(tags: ITagDescriptions) {
  const findOrCreateHeadNode = (tagName: string, attributes: IAttributesType): Element => {
    const attributesQuery = createAttributesQuery(attributes);

    let node = document.querySelector(`${tagName}${attributesQuery}`);

    if (!node) {
      const newNode = node = document.createElement(tagName);

      Object.entries(attributes).forEach(([k, v]) => {
        if (v !== undefined) {
          newNode.setAttribute(k, v);
        }
      });
    }

    if (!document.head) {
      throw new Error('Document <head> tag is undefined.');
    }

    if (!document.head.contains(node)) {
      // Make sure the meta tags are at the beginning of the <head>, because some services (e.g. LINE)
      // seem to give up searching for the meta tags if they're not near the beginning.
      document.head.insertBefore(node, document.head.firstChild);
    }

    return node;
  };

  const createAttributesQuery = (attributes: IAttributesType) =>
    Object.entries(attributes).map(([k, v]) => `[${k}="${v}"]`).join('');

  // Create and update tags.
  Object.keys(tags).forEach(key => {
    const description = tags[key];
    if (!description) {
      return;
    }

    const { tag, attributes } = description;
    // `<title>` is special because it is set via `document.title` instead of an
    // actual tag.
    if (tag === 'title') {
      const nextTitle = attributes.children || '';
      if (document.title !== nextTitle) {
        document.title = nextTitle;
      }
      return;
    }
    const node = findOrCreateHeadNode(tag, { [ID_ATTRIBUTE]: key });
    Object.keys(attributes).forEach(attributeName => {
      const attributeValue = attributes[attributeName];
      if (attributeValue === undefined && node.hasAttribute(attributeName)) {
        node.removeAttribute(attributeName);
      } else if (attributeValue !== undefined && node.getAttribute(attributeName) !== attributeValue) {
        node.setAttribute(attributeName, attributeValue);
      }
    });
  });

  // Remove old tags.
  const allNodes = [...document.querySelectorAll(`[${ID_ATTRIBUTE}]`)];
  for (const node of allNodes) {
    const key = node.getAttribute(ID_ATTRIBUTE);
    if (key && !tags[key]) {
      node.parentNode?.removeChild(node);
    }
  }
}
